import React, { useState, useCallback } from 'react'
import { AssistantHint } from 'src/components/assistant-hint'
import { useTranslation } from 'react-i18next'
import { ExpenseGroup, expenseApi } from 'src/services/expenses'
import { expenseQueries } from 'src/services/expenses/queries'
import { Button } from 'src/components/button'
import {
  CheckIcon,
  ClipboardDocumentIcon,
  DocumentDuplicateIcon,
  PencilIcon,
  ScissorsIcon,
  TrashIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { Receipt } from 'src/services/receipts'
import { PdfPreview } from 'src/components/pdf-preview'
import { ExpenseSidebarActionWrapper } from './expense-sidebar-action-wrapper'
import { ExpenseTimestamps } from './expense-timestamps'
import { Gate } from 'src/components/gate'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'
import { formatDateOnly } from 'src/utils/dates'

interface ExpensePreviewSidebarProps {
  expense: ExpenseGroup | null
  onClose: () => void
  onEdit: (id: string) => void
  onCreateCopy: (id: string) => void
  onCopyAndSplit: (expense: ExpenseGroup) => void
  isOpen: boolean
}

const getFileExtension = (url: string): string => {
  try {
    const urlWithoutQuery = url.split('?')[0]
    const filename = urlWithoutQuery.split('/').pop() || ''
    const extension = filename.split('.').pop() || ''
    return extension.toLowerCase()
  } catch (error) {
    console.error('Error extracting file extension:', error)
    return ''
  }
}

export const ExpensePreviewSidebar: React.FC<ExpensePreviewSidebarProps> = ({
  expense,
  onClose,
  onEdit,
  onCreateCopy,
  onCopyAndSplit,
  isOpen,
}) => {
  const { t } = useTranslation()
  const [previewingReceipt, setPreviewingReceipt] = useState<Receipt | null>(null)
  const [idCopied, setIdCopied] = useState(false)

  const close = () => {
    setPreviewingReceipt(null)
    onClose()
  }

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: expenseApi.remove,
    queryKey: expenseQueries.all(),
    onSuccess: close,
  })

  const handleCopyId = useCallback(() => {
    if (!expense) return
    navigator.clipboard.writeText(expense.id)
    setIdCopied(true)
    setTimeout(() => setIdCopied(false), 2000)
  }, [expense])

  const handleRemove = () => {
    if (expense) {
      requestDelete(expense.id, expense.name)
    }
  }

  const handleViewReceipt = (receipt: Receipt) => {
    setPreviewingReceipt(receipt)
  }

  const handleClosePreview = () => {
    setPreviewingReceipt(null)
  }

  if (!expense) return null

  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: expense.amountCurrency,
  })

  const fileExtension = previewingReceipt ? getFileExtension(previewingReceipt.asset) : ''
  const isPdf = fileExtension === 'pdf'

  return (
    <>
      {/* Overlay */}
      <div
        className={clsx('fixed inset-0 z-modal bg-black bg-opacity-50 transition-opacity', {
          'visible opacity-100': isOpen,
          'invisible opacity-0': !isOpen,
        })}
        onClick={close}
      />

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed right-0 top-0 z-modal h-full w-96 transform bg-surface shadow-lg transition-transform duration-300 ease-in-out',
          {
            'translate-x-0': isOpen,
            'translate-x-full': !isOpen,
          },
        )}
        data-testid="expense-preview-sidebar"
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b p-4">
            <h2 className="text-lg font-semibold">{t('expenseForm.expenseDetails')}</h2>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                tone="neutral"
                iconOnly
                onClick={handleCopyId}
                tooltip={idCopied ? t('expensePreview.idCopied') : t('expensePreview.copyId')}
              >
                {idCopied ? (
                  <CheckIcon className="h-4 w-4 text-green-500" aria-hidden="true" />
                ) : (
                  <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
              <Gate permission="expenses.delete">
                <Button
                  variant="danger"
                  iconOnly
                  tooltip={t('expensePreview.deleteExpense')}
                  onClick={handleRemove}
                  data-testid="remove-expense-button"
                >
                  <TrashIcon className="h-5 w-5" aria-hidden="true" />
                </Button>
              </Gate>
              <Button
                onClick={close}
                variant="ghost"
                tone="neutral"
                iconOnly
                tooltip={t('common.close')}
                type="button"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </Button>
            </div>
          </div>

          {/* Preview Pane */}
          {previewingReceipt && (
            <div className="border-b p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t('expenseForm.receiptPreview')}</h3>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    tone="neutral"
                    iconOnly
                    type="button"
                    onClick={() => window.open(previewingReceipt.asset, '_blank')}
                    tooltip={t('common.openInNewTab')}
                  >
                    <ArrowTopRightOnSquareIcon className="h-5 w-5" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    tone="neutral"
                    iconOnly
                    type="button"
                    onClick={handleClosePreview}
                    tooltip={t('expensePreview.closePreview')}
                  >
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </div>
              </div>
              <div className="mt-4 h-64 overflow-auto rounded border">
                {isPdf ? (
                  <PdfPreview
                    file={previewingReceipt.asset}
                    cacheKey={previewingReceipt.id}
                    allPages
                    width={350}
                    pageClassName="mb-4 shadow-sm"
                  />
                ) : (
                  <img
                    src={previewingReceipt.asset}
                    alt={previewingReceipt.name}
                    className="h-auto w-full"
                  />
                )}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              {/* Administrative Details */}
              <div>
                <p className="text-sm text-text-placeholder">{t('expenseForm.nameLabel')}</p>
                <p className="font-semibold" data-testid="sidebar-expense-name">
                  {expense.name}
                </p>
              </div>
              <div>
                <p className="text-sm text-text-placeholder">{t('expenseForm.amountLabel')}</p>
                <p className="text-lg font-semibold" data-testid="sidebar-expense-amount">
                  {currencyFormatter.format(Number(expense.amount))}
                </p>
                {expense.defaultConvertedAmount != null &&
                  expense.defaultConvertedAmountCurrency && (
                    <p
                      className="mt-1 text-sm text-text-placeholder"
                      data-testid="sidebar-expense-converted-amount"
                    >
                      {t('expenseForm.converted')}{' '}
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: expense.defaultConvertedAmountCurrency,
                      }).format(Number(expense.defaultConvertedAmount))}{' '}
                      {expense.defaultConvertedAmountCurrency}
                      {expense.convertedAmountIsManual && (
                        <span
                          className="ml-1 text-text-placeholder"
                          data-testid="sidebar-expense-converted-amount-manual"
                        >
                          ({t('expenseForm.manuallySet', { defaultValue: 'Manually set' })})
                        </span>
                      )}
                    </p>
                  )}
              </div>
              <div>
                <p className="text-sm text-text-placeholder">{t('expenseForm.expenseDateLabel')}</p>
                <p data-testid="sidebar-expense-date">{formatDateOnly(expense.expenseDate)}</p>
              </div>
              {expense.categoryRef && (
                <div>
                  <p className="text-sm text-text-placeholder">{t('expenseForm.categoryLabel')}</p>
                  <p data-testid="sidebar-expense-category">{expense.categoryRef.label}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-text-placeholder">
                  {t('expenseForm.isReimbursableLabel')}
                </p>
                <p data-testid="sidebar-expense-reimbursable">
                  {expense.isReimbursable
                    ? t('expenseForm.reimbursableYesShort')
                    : t('expenseForm.reimbursableNoShort')}
                </p>
              </div>
              {expense.notes && (
                <div>
                  <p className="text-sm text-text-placeholder">{t('expenseForm.notesLabel')}</p>
                  <p data-testid="sidebar-expense-notes">{expense.notes}</p>
                </div>
              )}
              {expense.organizationRef && (
                <div>
                  <p className="text-sm text-text-placeholder">
                    {t('expenseForm.organizationLabel')}
                  </p>
                  <p data-testid="sidebar-expense-organization">{expense.organizationRef.name}</p>
                </div>
              )}
              {expense.reportsRef && expense.reportsRef.length > 0 && (
                <div>
                  <p className="mb-2 text-sm text-text-placeholder">
                    {t('expenseForm.reports')}{' '}
                    {expense.reportsRef.length > 1 && `(${expense.reportsRef.length})`}
                  </p>
                  <div className="space-y-1" data-testid="sidebar-expense-reports">
                    {expense.reportsRef.map((report) => (
                      <div
                        key={report.id}
                        className="rounded bg-primary-50 px-2 py-1 text-sm text-primary-700"
                      >
                        {report.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {expense.receiptsRef && expense.receiptsRef.length > 0 && (
                <div>
                  <p className="mb-2 text-sm text-text-placeholder">
                    {t('expenseForm.receiptsLabel')}
                  </p>
                  <div className="space-y-2">
                    {expense.receiptsRef.map((receipt) => (
                      <ExpenseSidebarActionWrapper
                        key={receipt.id}
                        receipt={receipt}
                        onView={handleViewReceipt}
                      />
                    ))}
                  </div>
                </div>
              )}
              {/* Read-only audit metadata, visually separated from the details above */}
              <ExpenseTimestamps
                created={expense.created}
                lastEdited={expense.lastEdited}
                className="border-t pt-4"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="space-y-3 border-t p-4">
            <AssistantHint id="expense-split" text={t('assistantHint.expenseSplit')} />
            <div className="flex space-x-2">
              <Gate permission="expenses.edit">
                <Button variant="primary" onClick={() => onEdit(expense.id)} className="w-full">
                  <PencilIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                  {t('common.edit')}
                </Button>
              </Gate>
              <Gate permission="expenses.create">
                <Button
                  variant="secondary"
                  iconOnly
                  tooltip={t('expensePreview.duplicateExpense')}
                  onClick={() => onCreateCopy(expense.id)}
                  className="shrink-0"
                  data-testid="duplicate-expense-button"
                >
                  <DocumentDuplicateIcon className="h-5 w-5" aria-hidden="true" />
                </Button>
              </Gate>
              <Gate permission="expenses.create">
                <Button
                  variant="secondary"
                  onClick={() => onCopyAndSplit(expense)}
                  className="w-full"
                  data-testid="copy-and-split-button"
                >
                  <ScissorsIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                  {t('expenseForm.copyAndSplitBtn')}
                </Button>
              </Gate>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDeleteDialog {...dialogProps} />
    </>
  )
}
